import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { C, S } from './cedsStyles';

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function WorksheetForm({ assignment, existingSubmission, memberId, onSubmit }) {
  const [contacts, setContacts]   = useState(existingSubmission?.contacts_consulted ?? '');
  const [learned, setLearned]     = useState(existingSubmission?.what_learned ?? '');
  const [number, setNumber]       = useState(existingSubmission?.proposed_number ?? '');
  const [reasoning, setReasoning] = useState(existingSubmission?.proposed_reasoning ?? '');
  const [risk, setRisk]           = useState(existingSubmission?.biggest_risk ?? '');
  const [geoNote, setGeoNote]     = useState(existingSubmission?.geographic_note ?? '');
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState('');
  const [success, setSuccess]     = useState(false);

  async function handleSubmit() {
    setErr('');
    if (!number.trim())    { setErr('Proposed Target is required.'); return; }
    if (!reasoning.trim()) { setErr('Your Reasoning is required.'); return; }

    setSaving(true);

    if (existingSubmission) {
      await supabase
        .from('ceds_worksheet_submissions')
        .update({ status: 'superseded' })
        .eq('id', existingSubmission.id);
    }

    const { error } = await supabase.from('ceds_worksheet_submissions').insert({
      assignment_id:      assignment.id,
      member_id:          memberId,
      contacts_consulted: contacts.trim() || null,
      what_learned:       learned.trim() || null,
      proposed_number:    number.trim(),
      proposed_reasoning: reasoning.trim(),
      biggest_risk:       risk.trim() || null,
      geographic_note:    geoNote.trim() || null,
      status:             'submitted',
    });

    setSaving(false);
    if (error) { setErr(`Error: ${error.message}`); return; }
    setSuccess(true);
    onSubmit();
  }

  return (
    <div>
      {/* Context callout */}
      <div style={{
        background: C.cream, borderLeft: `4px solid ${C.gold}`,
        padding: '16px 20px', marginBottom: 24, borderRadius: 2,
      }}>
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted }}>CEDS Goal: </span>
          <span style={{ fontSize: 14, color: C.navy, fontWeight: 600 }}>{assignment.ceds_goal}</span>
        </div>
        <div style={{ marginBottom: assignment.padd_data ? 8 : 0 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted }}>Current draft language: </span>
          <span style={{ fontSize: 14, color: C.muted, fontStyle: 'italic' }}>{assignment.placeholder_text}</span>
        </div>
        {assignment.padd_data && (
          <div>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted }}>Data provided by PADD: </span>
            <span style={{ fontSize: 14, color: C.navy }}>{assignment.padd_data}</span>
          </div>
        )}
      </div>

      <p style={{ fontWeight: 700, fontSize: 15, color: C.navy, marginBottom: 24, lineHeight: 1.5 }}>
        {assignment.question_prompt}
      </p>

      {existingSubmission && (
        <div style={{
          background: C.goldLight, padding: '10px 14px', borderRadius: 4,
          marginBottom: 20, fontSize: 13, color: C.navy,
        }}>
          You submitted this on {fmtDate(existingSubmission.submitted_at)}. Saving will replace your previous answers.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div>
          <label style={S.label}>Who did you talk to?</label>
          <textarea value={contacts} onChange={e => setContacts(e.target.value)} rows={3}
            style={{ ...S.input, resize: 'vertical' }}
            placeholder="Names, titles, organizations consulted…" />
        </div>

        <div>
          <label style={S.label}>What did you learn?</label>
          <textarea value={learned} onChange={e => setLearned(e.target.value)} rows={3}
            style={{ ...S.input, resize: 'vertical' }}
            placeholder="Key takeaways from your conversations…" />
        </div>

        <div>
          <label style={S.label}>Your Proposed Target *</label>
          <input value={number} onChange={e => setNumber(e.target.value)}
            style={S.input} placeholder="e.g. 12 units per year, 25 slots, etc." />
        </div>

        <div>
          <label style={S.label}>Your Reasoning (1–3 sentences) *</label>
          <textarea value={reasoning} onChange={e => setReasoning(e.target.value)} rows={4}
            style={{ ...S.input, resize: 'vertical' }}
            placeholder="Why this number? What data or experience supports it?" />
        </div>

        <div>
          <label style={S.label}>Biggest Risk to This Target</label>
          <textarea value={risk} onChange={e => setRisk(e.target.value)} rows={3}
            style={{ ...S.input, resize: 'vertical' }}
            placeholder="What could prevent us from hitting this number?" />
        </div>

        <div>
          <label style={S.label}>Geographic Note (frontier vs. Scotts Bluff County)</label>
          <textarea value={geoNote} onChange={e => setGeoNote(e.target.value)} rows={3}
            style={{ ...S.input, resize: 'vertical' }}
            placeholder="How does this target break down across the region?" />
        </div>
      </div>

      {err && <div style={{ color: C.red, fontSize: 13, marginTop: 16 }}>{err}</div>}
      {success && (
        <div style={{ color: C.green, fontSize: 14, marginTop: 16, fontWeight: 600 }}>
          Submitted. You can update anytime before Meeting 2.
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={saving}
        style={{ ...S.btn.primary, marginTop: 20, opacity: saving ? 0.65 : 1 }}
      >
        {saving ? 'Saving…' : existingSubmission ? 'Update Submission' : 'Submit'}
      </button>
    </div>
  );
}
